import React from 'react';
import { Filter } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { TimesheetReportFilters } from '@/types/timesheetReport';
import { cn } from '@/lib/utils';
import { useSettings } from '@/contexts/SettingsContext';

interface QuickFiltersProps {
  filters: TimesheetReportFilters;
  onFiltersChange: (filters: TimesheetReportFilters) => void;
  onAdvancedClick: () => void;
  advancedFilterCount: number;
  customActions?: React.ReactNode;
}

export const QuickFilters: React.FC<QuickFiltersProps> = ({
  filters,
  onFiltersChange,
  onAdvancedClick,
  advancedFilterCount,
  customActions,
}) => {
  const { weekStartDay } = useSettings();

  // Date range options
  const getDateRange = (range: string) => {
    const today = new Date();
    const currentDay = today.getDay();
    // Days since the configured week start (handles wrap-around)
    const daysSinceWeekStart = (currentDay - weekStartDay + 7) % 7;
    
    switch (range) {
      case 'this-week': {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - daysSinceWeekStart);
        weekStart.setHours(0, 0, 0, 0);
        
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        
        return { dateFrom: weekStart, dateTo: weekEnd };
      }
      case 'last-week': {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - daysSinceWeekStart - 7);
        weekStart.setHours(0, 0, 0, 0);
        
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        
        return { dateFrom: weekStart, dateTo: weekEnd };
      }
      case 'this-month': {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        lastDay.setHours(23, 59, 59, 999);
        
        return { dateFrom: firstDay, dateTo: lastDay };
      }
      case 'last-month': {
        const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
        lastDay.setHours(23, 59, 59, 999);
        
        return { dateFrom: firstDay, dateTo: lastDay };
      }
      default:
        return {};
    }
  };

  const handleDateRangeChange = (value: string) => {
    if (value === 'all') {
      const { dateFrom, dateTo, quickDateRange, ...rest } = filters;
      onFiltersChange(rest);
    } else if (value === 'custom') {
      // Open advanced filters for custom date range
      onAdvancedClick();
    } else {
      const dateRange = getDateRange(value);
      onFiltersChange({ ...filters, ...dateRange, quickDateRange: value });
    }
  };

  const handleStatusChange = (status: string) => {
    const currentStatuses = filters.statuses || [];
    let newStatuses: typeof currentStatuses;
    
    if (status === 'all') {
      newStatuses = [];
    } else if (currentStatuses.includes(status as any)) {
      newStatuses = currentStatuses.filter(s => s !== status);
    } else {
      newStatuses = [...currentStatuses, status as any];
    }
    
    onFiltersChange({ ...filters, statuses: newStatuses.length > 0 ? newStatuses : undefined });
  };

    const handleEmployeeChange = (employeeId: string) => {
      if (employeeId === 'all') {
        const { employeeIds, ...rest } = filters;
        onFiltersChange(rest);
      } else {
        onFiltersChange({ ...filters, employeeIds: [parseInt(employeeId, 10)] });
      }
    };

  const handleProjectChange = (projectId: string) => {
    if (projectId === 'all') {
      const { projectIds, ...rest } = filters;
      onFiltersChange(rest);
    } else {
      onFiltersChange({ ...filters, projectIds: [projectId] });
    }
  };

  // Get current date range value
  const getCurrentDateRange = () => {
    if (filters.quickDateRange) return filters.quickDateRange;
    if (!filters.dateFrom && !filters.dateTo) return 'all';
    return 'custom';
  };

  return (
    <div className="w-full">
      <div className="flex items-center gap-3 flex-wrap w-full">
        {/* Date Range */}
        <Select
          value={getCurrentDateRange()}
          onValueChange={handleDateRangeChange}
        >
          <SelectTrigger className="w-auto h-9 bg-white px-3" data-tour="reports-date-filter">
            <SelectValue placeholder="Date Range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="this-week">This Week</SelectItem>
            <SelectItem value="last-week">Last Week</SelectItem>
            <SelectItem value="this-month">This Month</SelectItem>
            <SelectItem value="last-month">Last Month</SelectItem>
            <SelectItem value="custom">Custom Range...</SelectItem>
          </SelectContent>
        </Select>

        {/* Status */}
        <Select
          value={filters.statuses?.length === 1 ? filters.statuses[0] : filters.statuses?.length ? 'multiple' : 'all'}
          onValueChange={handleStatusChange}
        >
          <SelectTrigger className="w-auto h-9 bg-white px-3">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="locked">Locked</SelectItem>
            {filters.statuses && filters.statuses.length > 1 && (
              <SelectItem value="multiple">Multiple ({filters.statuses.length})</SelectItem>
            )}
          </SelectContent>
        </Select>

        {/* Spacer */}
        <div className="flex-1 min-w-[4px]" />

        {/* Advanced Filters Button - Responsive */}
        <Button
          variant="outline"
          onClick={onAdvancedClick}
          className="h-9 w-9 p-0 relative"
          data-tour="reports-advanced-filters"
        >
          <Filter className="h-4 w-4" />
        </Button>

        {/* Custom Actions (e.g., Field Selector) */}
        {customActions}
      </div>
    </div>
  );
};
